import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Keyboard + dismiss behaviour for the custom listbox popovers used by the
 * property and apartment switchers.
 *
 * A native `<select>` provides arrow-key navigation, Home/End, Enter to commit
 * and Escape to dismiss for free. These popovers are plain buttons, so that
 * behaviour has to be restored explicitly or keyboard users silently lose it.
 *
 * Focus stays on the trigger while the menu is open (the listbox carries
 * `aria-activedescendant`) rather than moving DOM focus between rows, so Tab
 * still leaves the control naturally instead of being trapped.
 */
export interface ListboxKeyboardOptions {
  /** Number of rows currently rendered. */
  itemCount: number;
  /** Whether the menu is open. */
  isOpen: boolean;
  /** Opens or closes the menu. */
  setIsOpen: (next: boolean) => void;
  /** Called when a row is committed. */
  onSelect: (index: number) => void;
  /** Index of the committed item; seeds the active row each time it opens. */
  selectedIndex?: number;
}

export function useListboxKeyboard({
  itemCount,
  isOpen,
  setIsOpen,
  onSelect,
  selectedIndex = -1,
}: ListboxKeyboardOptions) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(selectedIndex);

  // Seed the active row to the committed item whenever the menu opens.
  useEffect(() => {
    if (isOpen) setActiveIndex(selectedIndex);
  }, [isOpen, selectedIndex]);

  // Dismiss on outside click.
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, setIsOpen]);

  /** Keys handled while the menu is closed, on the trigger button. */
  const onTriggerKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setIsOpen(false);
        return;
      }
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        setActiveIndex(selectedIndex);
        setIsOpen(true);
        return;
      }
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        setIsOpen(!isOpen);
      }
    },
    [isOpen, selectedIndex, setIsOpen]
  );

  /** Keys handled while the menu is open, on the listbox. */
  const onMenuKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Escape' || event.key === 'Tab') {
        // Tab is allowed to move focus onward; Escape stays put.
        if (event.key === 'Escape') event.preventDefault();
        setIsOpen(false);
        return;
      }
      if (itemCount === 0) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setActiveIndex((prev) => (prev + 1) % itemCount);
          break;
        case 'ArrowUp':
          event.preventDefault();
          setActiveIndex((prev) => (prev - 1 + itemCount) % itemCount);
          break;
        case 'Home':
          event.preventDefault();
          setActiveIndex(0);
          break;
        case 'End':
          event.preventDefault();
          setActiveIndex(itemCount - 1);
          break;
        case 'Enter':
        case ' ':
          event.preventDefault();
          onSelect(activeIndex);
          setIsOpen(false);
          break;
        default:
          break;
      }
    },
    [activeIndex, itemCount, onSelect, setIsOpen]
  );

  return { containerRef, activeIndex, onTriggerKeyDown, onMenuKeyDown };
}